import { useState } from 'react';
import { DatePicker, Descriptions, Flex } from 'antd';
import dayjs from 'dayjs';
import { useLazyExportProfitXlsxQuery, useProfitReportQuery } from '@/entities/reports';
import type { ProfitReportLine } from '@/entities/reports';
import { ReportPage } from '@/shared/ui';
import { useStoreContext } from '@/shared/lib/store-context';
import { todayInTz } from '@/shared/lib/date';
import { displaySigned } from '@/shared/lib/money';

const columns = [
  {
    title: '',
    dataIndex: 'label',
    render: (label: string, line: ProfitReportLine) => (line.kind === 'line' ? label : <b>{label}</b>),
  },
  {
    title: 'Сумма',
    dataIndex: 'amount',
    width: 160,
    align: 'right' as const,
    render: (amount: string, line: ProfitReportLine) => (line.kind === 'line' ? displaySigned(amount) : <b>{displaySigned(amount)}</b>),
  },
];

/** §13, §11.3–11.5: точная прибыль за период. Строки и знаки приходят с сервера — те же, что в
 *  файле Excel: вычитаемые статьи с минусом, итог равен сумме строк. */
export function ProfitReportPage() {
  const ctx = useStoreContext();
  const today = todayInTz(ctx.timezone);
  const [from, setFrom] = useState(dayjs(today).startOf('month').format('YYYY-MM-DD'));
  const [to, setTo] = useState(today);
  const { data, isFetching } = useProfitReportQuery({ from, to }, { pollingInterval: 30_000 });
  const [exportXlsx] = useLazyExportProfitXlsxQuery();

  // Итог показан отдельно, под таблицей.
  const rows = (data?.lines ?? []).filter((line) => line.kind !== 'total');

  return (
    <ReportPage
      title="Прибыль"
      filters={
        <DatePicker.RangePicker
          format="DD.MM.YYYY"
          value={[dayjs(from), dayjs(to)]}
          onChange={(dates) => {
            if (dates && dates[0] && dates[1]) {
              setFrom(dates[0].format('YYYY-MM-DD'));
              setTo(dates[1].format('YYYY-MM-DD'));
            }
          }}
        />
      }
      summary={
        data ? (
          <Flex justify="flex-end" className="no-print">
            <Descriptions size="small" column={1} bordered style={{ maxWidth: 360 }}>
              <Descriptions.Item label="Итого прибыль">
                <b>{displaySigned(data.totalProfit)}</b>
              </Descriptions.Item>
            </Descriptions>
          </Flex>
        ) : null
      }
      columns={columns}
      data={rows}
      loading={isFetching}
      rowKey="key"
      onExport={() => exportXlsx({ from, to }).unwrap()}
      exportFilename="Прибыль.xlsx"
    />
  );
}
